// src/components/Teacher/StudentSubmissions.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
 
// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
    primary:            '#6a37d4',
    primaryLight:       '#ae8dff',
    primaryContainer:   '#ede8ff',
    secondary:          '#006947',
    secondaryContainer: '#d1fae5',
    tertiary:           '#6f5900',
    tertiaryContainer:  '#fed01b',
    surface:            '#f3f6ff',
    surfaceLow:         '#eaf1ff',
    surfaceHigh:        '#d6e3f9',
    surfaceLowest:      '#ffffff',
    onBackground:       '#252f3c',
    onSurface:          '#252f3c',
    onSurfaceVariant:   '#525c6a',
    outlineVariant:     '#a4aebe',
    error:              '#b41340',
    errorContainer:     '#ffd9df',
};
 
const SIDEBAR_WIDTH = 220;
 
interface Story {
    id: string;
    title: string;
    status: 'draft' | 'published';
    submittedAt: Date | null;
    lastEditedAt: Date | null;
}
 
interface StudentData {
    displayName: string;
    email: string;
}
 
const StudentSubmissions: React.FC = () => {
    const navigate = useNavigate();
    const { classId, studentId } = useParams<{ classId: string; studentId: string }>();
    const { currentUser } = useAuth();
    const [student, setStudent]   = useState<StudentData | null>(null);
    const [stories, setStories]   = useState<Story[]>([]);
    const [showDrafts, setShowDrafts] = useState(false);
    const [loading, setLoading]   = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
 
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
 
    // ── Data loading (logic unchanged, debug logs removed) ────────────────────
    useEffect(() => {
        const loadStudentData = async () => {
            if (!currentUser || !classId || !studentId) return;
            try {
                // Verify teacher owns this class
                const classDoc = await getDoc(doc(db, 'classes', classId));
                if (!classDoc.exists() || classDoc.data().teacherId !== currentUser.uid) {
                    alert('Access denied');
                    navigate('/dashboard');
                    return;
                }
 
                // Load student info
                const studentDoc = await getDoc(doc(db, 'users', studentId));
                if (!studentDoc.exists()) {
                    alert('Student not found');
                    navigate(`/teacher/class/${classId}`);
                    return;
                }
                const sd = studentDoc.data();
                setStudent({ displayName: sd.displayName || 'Student', email: sd.email || '' });
 
                // Load student's published stories for this class
                const storiesSnap = await getDocs(query(
                    collection(db, 'stories'),
                    where('userId', '==', studentId),
                    where('classId', '==', classId),
                    where('status', '==', 'published')
                ));
 
                const loaded: Story[] = storiesSnap.docs.map(d => {
                    const data = d.data();
                    return {
                        id: d.id,
                        title: data.title || 'Untitled Story',
                        status: data.status || 'draft',
                        submittedAt:  data.submittedAt?.toDate()  || null,
                        lastEditedAt: data.lastEditedAt?.toDate() || null,
                    };
                });
 
                loaded.sort((a, b) => {
                    const da = a.submittedAt || a.lastEditedAt || new Date(0);
                    const db_ = b.submittedAt || b.lastEditedAt || new Date(0);
                    return db_.getTime() - da.getTime();
                });
 
                setStories(loaded);
            } catch (err: any) {
                console.error('Error loading student data:', err);
            } finally {
                setLoading(false);
            }
        };
        loadStudentData();
    }, [currentUser, classId, studentId, navigate]);
 
    const getRelativeTime = (date: Date | null): string => {
        if (!date) return 'Never';
        const diffMs    = Date.now() - date.getTime();
        const diffMins  = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays  = Math.floor(diffMs / 86400000);
        if (diffMins < 1)   return 'Just now';
        if (diffMins < 60)  return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays === 1) return '1d ago';
        if (diffDays < 30)  return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };
 
    const filteredStories = showDrafts
        ? stories
        : stories.filter(s => s.status === 'published');
 
    // ── Loading ───────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: C.surface, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '16px' }}>📚</div>
                    <div style={{ fontSize: '1rem', color: C.onSurfaceVariant, fontWeight: '600' }}>Loading submissions...</div>
                </div>
            </div>
        );
    }
 
    return (
        <div style={{ minHeight: '100vh', background: C.surface, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <div style={{
                marginLeft: isMobile ? 0 : `${SIDEBAR_WIDTH}px`,
                padding: isMobile ? '20px 16px 80px' : '40px 48px',
                boxSizing: 'border-box', overflowX: 'hidden',
            }}>
 
                {/* ── Header ──────────────────────────────────────────────── */}
                <div style={{ marginBottom: '32px' }}>
                {/* ── Breadcrumb ───────────────────────────────────────────── */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '20px', flexWrap: 'wrap' }}>
                    <button onClick={() => navigate('/dashboard')}
                        style={{ background: 'none', border: 'none', color: C.primary, fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", padding: 0 }}>
                        Dashboard
                    </button>
                    <span style={{ color: C.outlineVariant, fontSize: '0.82rem' }}>›</span>
                    <button onClick={() => navigate(`/teacher/class/${classId}`)}
                        style={{ background: 'none', border: 'none', color: C.primary, fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", padding: 0 }}>
                        Class
                    </button>
                    <span style={{ color: C.outlineVariant, fontSize: '0.82rem' }}>›</span>
                    <span style={{ fontSize: '0.82rem', color: C.onSurfaceVariant, fontWeight: '600' }}>{student?.displayName || 'Student'}</span>
                </div>
 
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '8px' }}>
                        {/* Avatar */}
                        <div style={{ width: 48, height: 48, borderRadius: '50%', background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: '900', fontSize: '1.2rem', flexShrink: 0 }}>
                            {student?.displayName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <h1 style={{ fontFamily: "'Epilogue', sans-serif", fontSize: isMobile ? '1.6rem' : '2rem', fontWeight: '900', color: C.onBackground, margin: 0 }}>
                                {student?.displayName}
                            </h1>
                            <p style={{ fontSize: '0.85rem', color: C.onSurfaceVariant, margin: 0 }}>
                                {filteredStories.length} {showDrafts ? 'stories' : 'published stories'} for this class
                            </p>
                        </div>
                    </div>
                </div>
 
                {/* ── Filter toggle ────────────────────────────────────────── */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
                    {[
                        { label: 'Published', value: false },
                        { label: 'All (inc. Drafts)', value: true },
                    ].map(opt => (
                        <button
                            key={String(opt.value)}
                            onClick={() => setShowDrafts(opt.value)}
                            style={{ padding: '8px 18px', borderRadius: '9999px', border: 'none', background: showDrafts === opt.value ? C.primary : C.surfaceHigh, color: showDrafts === opt.value ? '#fff' : C.onSurfaceVariant, fontSize: '0.82rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s' }}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
 
                {/* ── Stories ──────────────────────────────────────────────── */}
                {filteredStories.length === 0 ? (
                    <div style={{ background: C.surfaceLowest, borderRadius: '20px', padding: '60px 32px', textAlign: 'center', border: `1px solid ${C.surfaceHigh}` }}>
                        <div style={{ fontSize: '3rem', marginBottom: '14px' }}>📝</div>
                        <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '900', fontSize: '1.2rem', color: C.onBackground, margin: '0 0 8px 0' }}>
                            No {showDrafts ? 'stories' : 'published stories'} yet
                        </h3>
                        <p style={{ fontSize: '0.9rem', color: C.onSurfaceVariant, margin: '0 0 20px 0' }}>
                            {showDrafts
                                ? 'This student has not created any stories for this class yet.'
                                : 'This student has not published any stories yet. Try showing drafts.'}
                        </p>
                        {!showDrafts && (
                            <button
                                onClick={() => setShowDrafts(true)}
                                style={{ background: C.primaryContainer, color: C.primary, border: 'none', borderRadius: '9999px', padding: '10px 22px', fontSize: '0.875rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif" }}
                            >
                                Show All Stories
                            </button>
                        )}
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {filteredStories.map(story => (
                            <div
                                key={story.id}
                                onClick={() => navigate(`/teacher/class/${classId}/story/${story.id}`)}
                                style={{ background: C.surfaceLowest, borderRadius: '18px', padding: '20px 24px', border: `1.5px solid ${C.surfaceHigh}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 2px 6px rgba(0,0,0,0.04)', flexWrap: 'wrap' }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = C.primary; (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = C.surfaceHigh; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 6px rgba(0,0,0,0.04)'; }}
                            >
                                <div style={{ flex: 1, minWidth: '160px' }}>
                                    <h3 style={{ fontFamily: "'Epilogue', sans-serif", fontWeight: '800', fontSize: '1rem', color: C.onBackground, margin: '0 0 6px 0' }}>
                                        {story.title}
                                    </h3>
                                    <div style={{ fontSize: '0.8rem', color: C.onSurfaceVariant }}>
                                        {story.submittedAt
                                            ? `Submitted ${getRelativeTime(story.submittedAt)}`
                                            : `Last edited ${getRelativeTime(story.lastEditedAt)}`}
                                    </div>
                                </div>
 
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                                    <span style={{
                                        background: story.status === 'published' ? C.secondaryContainer : C.tertiaryContainer,
                                        color: story.status === 'published' ? C.secondary : C.tertiary,
                                        padding: '4px 12px', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px',
                                    }}>
                                        {story.status === 'published' ? '✅ Published' : '✏️ Draft'}
                                    </span>
                                    <button
                                        onClick={e => { e.stopPropagation(); navigate(`/teacher/class/${classId}/story/${story.id}`); }}
                                        style={{ background: C.primaryContainer, color: C.primary, border: 'none', borderRadius: '9999px', padding: '8px 18px', fontSize: '0.8rem', fontWeight: '700', cursor: 'pointer', fontFamily: "'Epilogue', sans-serif", transition: 'all 0.15s', whiteSpace: 'nowrap' }}
                                        onMouseEnter={e => { e.currentTarget.style.background = C.primary; e.currentTarget.style.color = '#fff'; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = C.primaryContainer; e.currentTarget.style.color = C.primary; }}
                                    >
                                        Open Story →
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
 
                {/* ── Tip ─────────────────────────────────────────────────── */}
                <div style={{ marginTop: '24px', padding: '14px 18px', background: C.primaryContainer, borderRadius: '12px', fontSize: '0.82rem', color: C.primary, fontWeight: '600' }}>
                    💡 Click any story to read it and leave feedback for the student.
                </div>
            </div>
        </div>
    );
};
 
export default StudentSubmissions;